// src/routes/AppRoutes.jsx
// アプリ全体のルーティング。認証必要なルートは <Authed> でラップする。
// App.jsx は認証状態のみ管理し、ルーティングはここに集約。

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import Login              from '../pages/Login';
import StudentDashboard   from '../pages/StudentDashboard';
import StudentAttendance  from '../pages/StudentAttendance';
import TeacherDashboard   from '../pages/TeacherDashboard';
import OtpGenerate        from '../pages/OtpGenerate';
import ClassManagement    from '../pages/ClassManagement';
import ClassDetailPage    from '../pages/ClassDetailPage';
import StudentManagement  from '../pages/StudentManagement';
import Reports            from '../pages/Reports';
import ClassSummary       from '../pages/ClassSummary';
import LiveAttendance     from '../pages/LiveAttendance';
import Notifications      from '../pages/Notifications';
import AcademicDashboard  from '../pages/AcademicDashboard';
import CardManagement     from '../pages/CardManagement';
import UsersAudit         from '../pages/UsersAudit';
import RosterImports      from '../pages/RosterImports';
import ProfileEdit        from '../pages/ProfileEdit';
import SignupTeacher      from '../pages/SignupTeacher';
import AppDownload        from '../pages/AppDownload';
import SecurityStatus     from '../pages/SecurityStatus';
import DataRetention      from '../pages/DataRetention';
import AttendanceRequests from '../pages/AttendanceRequests';
import CSVImport          from '../components/CSVImport';
// メッセージ機能【2026-10-16】
import Messages               from '../pages/Messages';
import Notices                from '../pages/Notices';
import TeacherMessageSettings from '../pages/TeacherMessageSettings';
import TeacherAnnouncements   from '../pages/TeacherAnnouncements';
import Bulletins              from '../pages/Bulletins';
import MessagingAdmin         from '../pages/MessagingAdmin';

import RequireAuth from '../auth/RequireAuth';
import Layout      from '../layout/Layout';

// ロール別のホーム画面振り分け
function HomePage({ user }) {
  if (user.role === 'student')        return <StudentDashboard user={user} />;
  if (user.role === 'academic_staff') return <AcademicDashboard />;
  return <TeacherDashboard user={user} />;
}

export default function AppRoutes({ user, onLogin, onLogout, onUserUpdate }) {
  // 認証必要ルート用のラッパ
  const Authed = ({ children }) => (
    <RequireAuth user={user}>
      <Layout user={user} onLogout={onLogout}>{children}</Layout>
    </RequireAuth>
  );

  return (
    <Routes>
      <Route path="/login"          element={ user ? <Navigate to="/" replace /> : <Login         onLogin={onLogin} /> } />
      <Route path="/signup-teacher" element={ user ? <Navigate to="/" replace /> : <SignupTeacher onLogin={onLogin} /> } />

      <Route path="/"              element={<Authed><HomePage user={user}/></Authed>} />
      <Route path="/attendance"    element={<Authed><StudentAttendance/></Authed>} />
      <Route path="/otp"           element={<Authed><OtpGenerate user={user}/></Authed>} />
      <Route path="/live"          element={<Authed><LiveAttendance/></Authed>} />
      <Route path="/classes"       element={<Authed><ClassManagement/></Authed>} />
      {/* 授業詳細は独立ページ。授業名セレクトで他の授業へ移動できる【2026-09-04】
          サブメニューで「授業スケジュール」「受講者名簿」に分かれる（URLも分離） */}
      <Route path="/classes/:classId"          element={<Authed><ClassDetailPage/></Authed>} />
      <Route path="/classes/:classId/:section" element={<Authed><ClassDetailPage/></Authed>} />
      <Route path="/students"      element={<Authed><StudentManagement user={user}/></Authed>} />
      <Route path="/reports"       element={<Authed><Reports/></Authed>} />
      <Route path="/class-summary" element={<Authed><ClassSummary/></Authed>} />
      <Route path="/csv-import"    element={<Authed><CSVImport user={user}/></Authed>} />
      <Route path="/academic"      element={<Authed><AcademicDashboard/></Authed>} />
      <Route path="/cards"         element={<Authed><CardManagement user={user}/></Authed>} />
      <Route path="/notifications" element={<Authed><Notifications/></Authed>} />
      <Route path="/users-audit"   element={<Authed><UsersAudit/></Authed>} />
      {/* 名簿の取り込み履歴（年度の直し・取り消し）【2026-09-14】 */}
      <Route path="/roster-imports" element={<Authed><RosterImports/></Authed>} />
      <Route path="/app-download"  element={<Authed><AppDownload/></Authed>} />
      <Route path="/security"      element={<Authed><SecurityStatus user={user}/></Authed>} />
      {/* 年度の締め・保管（教務だけ。サーバー側でも教務以外は 403）【2026-09-17】 */}
      {/* 学生からの出欠の訂正・公欠の申請（先生・教務）【2026-09-30】 */}
      <Route path="/requests"      element={<Authed><AttendanceRequests/></Authed>} />
      <Route path="/data-retention" element={<Authed><DataRetention/></Authed>} />
      <Route path="/profile"       element={<Authed><ProfileEdit user={user} onUserUpdate={onUserUpdate}/></Authed>} />
      {/* メッセージ機能【2026-10-16】。/messages/settings などは /messages/:conversationId より先に書く */}
      <Route path="/messages/settings"      element={<Authed><TeacherMessageSettings user={user}/></Authed>} />
      <Route path="/messages/announcements" element={<Authed><TeacherAnnouncements user={user}/></Authed>} />
      <Route path="/messages/bulletins"     element={<Authed><Bulletins user={user}/></Authed>} />
      <Route path="/messages/admin"         element={<Authed><MessagingAdmin user={user}/></Authed>} />
      <Route path="/messages"               element={<Authed><Messages user={user}/></Authed>} />
      <Route path="/messages/:conversationId" element={<Authed><Messages user={user}/></Authed>} />
      <Route path="/notices"                element={<Authed><Notices user={user}/></Authed>} />

      <Route path="*" element={<Navigate to={user ? '/' : '/login'} replace />} />
    </Routes>
  );
}
